'use client';

import { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import InputBase from '@mui/material/InputBase';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

import { RADIUS, LOCK_ICON } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import FilterChip, { FILTER_CHIP_GRID_2_COL_SX } from 'src/components/cmui/FilterChip';

import type { HubSelectOption } from './hub-select';

/** 고를 것이 이보다 많으면 «검색 · 더보기»로 접는다 */
export const HUB_FILTER_SEARCH_AT = 12;

/** 접힌 채로 먼저 보이는 칩 수 — 고른 칩은 이 밖이어도 늘 보인다 */
const VISIBLE_CHIPS = 8;

/**
 * **필터 판의 칩 고르기** — 고를 것을 칩으로 바로 펴고, 한 번 누르면 토글한다(두 단계 메뉴가 없다).
 *
 * ```
 *   (KR 1.3만)(US 1만)
 *   (JP 2,861)(TW 812)
 *   [🔍 국가 검색 · 더보기 (16)]   ← 많은 축만 · 누르면 검색칸 + 전부
 * ```
 *
 * 🔑 값과 바꾸는 함수는 부르는 쪽 것이다 — `onPick` 은 **누른 값 하나**를 준다(`HubSelect` 와 같은 약속).
 *   하나 고르는 칸에서 고른 칩을 다시 누르면 `''`(「전체」)를 준다 — 「전체」가 없는 칸(`hideEmpty`)은 그대로 둔다.
 * 🔑 칩 옆 수는 `count` 가 있을 때만 선다 · 묶음(`group`)이 바뀌면 그 이름이 한 줄 선다.
 * ⚠️ 잠긴 칸(`locked`)은 자리를 남기고 자물쇠를 단다 — 누르면 `onLocked` 다(값은 그대로).
 * ⚠️ 못 고르는 칸(`disabled`)은 흐리게 남긴다 — 빼면 왜 없는지 알 길이 없다.
 */
export function HubFilterChips({
  label,
  options,
  value,
  multiple = false,
  hideEmpty = false,
  onPick,
  onLocked,
}: {
  /** 축 이름 — 검색칸 자리표시(「국가 검색」)에 쓴다 */
  label: string;
  options: readonly HubSelectOption[];
  value: string | readonly string[];
  multiple?: boolean;
  hideEmpty?: boolean;
  onPick: (next: string) => void;
  onLocked?: (value: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');

  const picked = useMemo(() => new Set(Array.isArray(value) ? value : [value].filter(Boolean)), [value]);
  const searchable = options.length > HUB_FILTER_SEARCH_AT;

  const shown = useMemo(() => {
    if (!searchable) return options;
    if (!expanded) return options.filter((option, index) => index < VISIBLE_CHIPS || picked.has(option.value));
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle));
  }, [options, searchable, expanded, search, picked]);

  const press = (option: HubSelectOption) => {
    if (option.disabled) return;
    if (option.locked) {
      onLocked?.(option.value);
      return;
    }
    if (!multiple && picked.has(option.value)) {
      if (!hideEmpty) onPick('');
      return;
    }
    onPick(option.value);
  };

  /* 묶음 이름이 바뀌는 자리마다 끊는다 — 차례는 부르는 쪽이 정한 대로다 */
  const runs = shown.reduce<{ group?: string; items: HubSelectOption[] }[]>((acc, option) => {
    const last = acc[acc.length - 1];
    if (last && last.group === option.group) last.items.push(option);
    else acc.push({ group: option.group, items: [option] });
    return acc;
  }, []);

  return (
    <Stack spacing={1}>
      {searchable && expanded && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, height: 32, borderRadius: RADIUS.sm, border: 1, borderColor: 'divider' }}>
          <Iconify icon="solar:magnifer-linear" width={15} sx={{ color: 'text.disabled' }} />
          <InputBase
            autoFocus
            fullWidth
            value={search}
            placeholder={`${label} 검색`}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            inputProps={{ 'aria-label': `${label} 검색` }}
            sx={{ typography: 'uiBody' }}
          />
        </Stack>
      )}

      <Box sx={searchable && expanded ? { maxHeight: 280, overflowY: 'auto' } : undefined}>
        <Stack spacing={1}>
          {runs.map((run, index) => (
            <Box key={`${run.group ?? ''}:${index}`}>
              {run.group && (
                <Typography sx={{ typography: 'uiLabel', color: 'text.secondary', mb: 0.5 }}>{run.group}</Typography>
              )}
              <Box sx={FILTER_CHIP_GRID_2_COL_SX}>
                {run.items.map((option) => (
                  <FilterChip
                    fullWidth
                    key={option.value}
                    aria-pressed={picked.has(option.value)}
                    title={option.label}
                    label={(
                      <>
                        {option.count == null && option.node ? option.node : option.label}
                        {option.locked && <Iconify icon={LOCK_ICON} width={12} sx={{ ml: 0.5, verticalAlign: 'middle' }} />}
                      </>
                    )}
                    count={option.count}
                    isSelected={picked.has(option.value)}
                    onClick={() => press(option)}
                    sx={option.disabled || option.locked ? { opacity: 0.45 } : undefined}
                  />
                ))}
              </Box>
            </Box>
          ))}
          {shown.length === 0 && (
            <Typography variant="uiMeta" sx={{ color: 'text.disabled', px: 0.5 }}>
              {options.length === 0 ? '고를 값이 없습니다.' : '맞는 값이 없습니다.'}
            </Typography>
          )}
        </Stack>
      </Box>

      {searchable && (
        <ButtonBase
          onClick={() => {
            setExpanded((prev) => !prev);
            setSearch('');
          }}
          sx={{
            height: 32,
            px: 1.5,
            gap: 1,
            justifyContent: 'flex-start',
            borderRadius: RADIUS.md,
            border: 1,
            borderColor: 'divider',
            typography: 'uiBody',
            color: 'text.secondary',
          }}
        >
          <Iconify icon={expanded ? 'solar:alt-arrow-up-linear' : 'solar:magnifer-linear'} width={14} />
          {expanded ? '접기' : `${label} 검색 · 더보기 (${options.length - shown.length})`}
        </ButtonBase>
      )}
    </Stack>
  );
}
